import React, { useState } from 'react';
import { FileText, Download, Info, Plus, Trash2 } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const PDFMetadataEditor = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [metadata, setMetadata] = useState({
        title: '',
        author: '',
        subject: '',
        keywords: '',
        creator: '',
        producer: '',
        creationDate: '',
        modificationDate: ''
    });
    const [customProperties, setCustomProperties] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);

                // Read existing metadata
                const title = pdf.getTitle() || '';
                const author = pdf.getAuthor() || '';
                const subject = pdf.getSubject() || '';
                const keywords = pdf.getKeywords() || '';
                const creator = pdf.getCreator() || '';
                const producer = pdf.getProducer() || '';
                const creationDate = pdf.getCreationDate();
                const modificationDate = pdf.getModificationDate();

                setMetadata({
                    title,
                    author,
                    subject,
                    keywords,
                    creator,
                    producer,
                    creationDate: creationDate ? creationDate.toISOString().slice(0, 16) : '',
                    modificationDate: modificationDate ? modificationDate.toISOString().slice(0, 16) : ''
                });

                toast.success('PDF loaded and metadata extracted');
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const handleMetadataChange = (field, value) => {
        setMetadata(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const addCustomProperty = () => {
        setCustomProperties(prev => [
            ...prev,
            { id: Date.now(), key: '', value: '' }
        ]);
    };

    const updateCustomProperty = (id, field, value) => {
        setCustomProperties(prev =>
            prev.map(prop =>
                prop.id === id ? { ...prop, [field]: value } : prop
            )
        );
    };

    const removeCustomProperty = (id) => {
        setCustomProperties(prev => prev.filter(prop => prop.id !== id));
    };

    const saveMetadata = async () => {
        if (!pdfDoc) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            // Set standard metadata
            if (metadata.title) pdfDoc.setTitle(metadata.title);
            if (metadata.author) pdfDoc.setAuthor(metadata.author);
            if (metadata.subject) pdfDoc.setSubject(metadata.subject);
            if (metadata.keywords) pdfDoc.setKeywords(metadata.keywords);
            if (metadata.creator) pdfDoc.setCreator(metadata.creator);
            if (metadata.producer) pdfDoc.setProducer(metadata.producer);

            setProgress(40);

            // Set dates
            if (metadata.creationDate) {
                pdfDoc.setCreationDate(new Date(metadata.creationDate));
            }
            if (metadata.modificationDate) {
                pdfDoc.setModificationDate(new Date(metadata.modificationDate));
            }

            setProgress(60);

            // Note: pdf-lib doesn't support custom properties directly
            // Custom properties would require direct PDF manipulation
            // For now, we'll add them as keywords if they exist
            if (customProperties.length > 0) {
                const customKeywords = customProperties
                    .filter(prop => prop.key && prop.value)
                    .map(prop => `${prop.key}:${prop.value}`)
                    .join('; ');

                if (customKeywords) {
                    const existingKeywords = metadata.keywords ? metadata.keywords + '; ' : '';
                    pdfDoc.setKeywords(existingKeywords + customKeywords);
                }
            }

            setProgress(80);

            // Save the PDF
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            // Download
            const timestamp = new Date().toISOString().slice(0, 10);
            saveAs(blob, `metadata-updated-${file.name}`);

            toast.success('PDF metadata updated successfully!');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Metadata update error:', error);
            toast.error(`Failed to update metadata: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Info size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF Metadata Editor
                </h1>
                <p className="tool-description">
                    Edit PDF document properties including title, author, subject, keywords, dates, and custom metadata.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPdfDoc(null);
                                setMetadata({
                                    title: '',
                                    author: '',
                                    subject: '',
                                    keywords: '',
                                    creator: '',
                                    producer: '',
                                    creationDate: '',
                                    modificationDate: ''
                                });
                                setCustomProperties([]);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Standard Metadata */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Document Information
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                                {/* Title */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.title}
                                        onChange={(e) => handleMetadataChange('title', e.target.value)}
                                        placeholder="Enter document title"
                                        className="input"
                                    />
                                </div>

                                {/* Author */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Author
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.author}
                                        onChange={(e) => handleMetadataChange('author', e.target.value)}
                                        placeholder="Enter author name"
                                        className="input"
                                    />
                                </div>

                                {/* Subject */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.subject}
                                        onChange={(e) => handleMetadataChange('subject', e.target.value)}
                                        placeholder="Enter document subject"
                                        className="input"
                                    />
                                </div>

                                {/* Keywords */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Keywords
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.keywords}
                                        onChange={(e) => handleMetadataChange('keywords', e.target.value)}
                                        placeholder="Enter keywords (comma-separated)"
                                        className="input"
                                    />
                                    <p style={{
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--color-text-tertiary)',
                                        marginTop: 'var(--spacing-1)'
                                    }}>
                                        Separate multiple keywords with commas
                                    </p>
                                </div>

                                {/* Creator */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Creator (Application)
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.creator}
                                        onChange={(e) => handleMetadataChange('creator', e.target.value)}
                                        placeholder="Enter creator application"
                                        className="input"
                                    />
                                </div>

                                {/* Producer */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        marginBottom: 'var(--spacing-2)',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        Producer
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.producer}
                                        onChange={(e) => handleMetadataChange('producer', e.target.value)}
                                        placeholder="Enter producer"
                                        className="input"
                                    />
                                </div>

                                {/* Dates */}
                                <div className="grid-2">
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: 'var(--text-sm)',
                                            fontWeight: 'var(--font-medium)',
                                            marginBottom: 'var(--spacing-2)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            Creation Date
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={metadata.creationDate}
                                            onChange={(e) => handleMetadataChange('creationDate', e.target.value)}
                                            className="input"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: 'var(--text-sm)',
                                            fontWeight: 'var(--font-medium)',
                                            marginBottom: 'var(--spacing-2)',
                                            color: 'var(--color-text-secondary)'
                                        }}>
                                            Modification Date
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={metadata.modificationDate}
                                            onChange={(e) => handleMetadataChange('modificationDate', e.target.value)}
                                            className="input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Custom Properties */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <h3 style={{
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: 'var(--font-bold)'
                                }}>
                                    Custom Properties
                                </h3>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={addCustomProperty}
                                >
                                    <Plus size={16} />
                                    Add Property
                                </Button>
                            </div>

                            {customProperties.length === 0 ? (
                                <Alert variant="info">
                                    No custom properties added. Click "Add Property" to create custom metadata fields.
                                </Alert>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                                    {customProperties.map((prop) => (
                                        <Card key={prop.id} variant="outlined">
                                            <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'start' }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="text"
                                                        value={prop.key}
                                                        onChange={(e) => updateCustomProperty(prop.id, 'key', e.target.value)}
                                                        placeholder="Property name"
                                                        className="input"
                                                        style={{ marginBottom: 'var(--spacing-2)' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={prop.value}
                                                        onChange={(e) => updateCustomProperty(prop.id, 'value', e.target.value)}
                                                        placeholder="Property value"
                                                        className="input"
                                                    />
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon
                                                    onClick={() => removeCustomProperty(prop.id)}
                                                >
                                                    <Trash2 size={16} color="var(--color-error)" />
                                                </Button>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}

                            {customProperties.length > 0 && (
                                <NoteCard variant="warning">
                                    <strong>Note:</strong> Custom properties will be added to the keywords field in the format "key:value".
                                </NoteCard>
                            )}
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Save Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={saveMetadata}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Save Metadata & Download
                        </Button>
                    </div>

                    {/* Info */}
                    <Alert variant="info" style={{ marginTop: 'var(--spacing-6)' }}>
                        <strong>Tip:</strong> All metadata fields are optional. Leave fields empty to remove existing metadata. The modified PDF will be downloaded with updated metadata.
                    </Alert>
                </>
            )}
        </div>
    );
};

export default PDFMetadataEditor;
